import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useFamily } from '@/contexts/FamilyContext';
import { authService, medicalRecordService } from '@/services/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Activity,
  Cake,
  Calendar,
  Camera,
  ChevronLeft,
  Droplets,
  Edit2,
  ImagePlus,
  HeartPulse,
  ListPlus,
  Loader2,
  Mail,
  Phone,
  Plus,
  Scale,
  Star,
  Trash2,
  UserRound,
  Users,
  X,
} from 'lucide-react';

const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const alcoholOptions = ['none', 'occasional', 'moderate', 'heavy'];
const exerciseOptions = ['none', 'rarely', 'weekly', 'daily'];

const createEmptyForm = () => ({
  name: '',
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  gender: '',
  dateOfBirth: '',
  bloodGroup: '',
  weight: '',
  height: '',
  smoker: false,
  smokingYears: '',
  alcoholConsumption: 'none',
  exerciseFrequency: 'none',
  familyHistory: '',
  chronicDiseases: [],
  permanentMedications: [],
  allergies: [],
  surgeries: [],
  vaccines: [],
  isDefault: false,
  imageFile: null,
  imagePreviewUrl: '',
});

const createEmptyTagInputs = () => ({
  chronicDiseases: '',
  permanentMedications: '',
  allergies: '',
  surgeries: '',
  vaccines: '',
});

const createEmptyMedicalOptions = () => ({
  chronicDiseases: [],
  permanentMedications: [],
  allergies: [],
  surgeries: [],
  vaccines: [],
});

const normalizePhone = (value) => {
  let phone = String(value || '').trim().replace(/\s+/g, '').replace(/-/g, '');
  if (phone.startsWith('+9630')) {
    phone = `+963${phone.slice(5)}`;
  } else if (phone.startsWith('009630')) {
    phone = `00963${phone.slice(6)}`;
  } else if (phone.startsWith('09')) {
    phone = `+963${phone.slice(1)}`;
  } else if (/^\d+$/.test(phone) && !phone.startsWith('963')) {
    phone = `+963${phone}`;
  } else if (phone.startsWith('963') && !phone.startsWith('+963')) {
    phone = `+${phone}`;
  }

  return phone;
};

const extractVerificationToken = (response) =>
  response?.data?.data?.verification_token ||
  response?.data?.verification_token ||
  '';

const shouldFallbackToAccountProfile = (error) => {
  if (error?.response?.status === 404) {
    return true;
  }

  const message = readString(
    error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message
  ).toLowerCase();

  return (
    message.includes('not found') ||
    message.includes('patient not found') ||
    message.includes('no patient')
  );
};

const readMap = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }

  return {};
};

const readList = (value) => (Array.isArray(value) ? value : []);

const readString = (value) => {
  if (value == null || value === false) {
    return '';
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    for (const key of ['name', 'display_name', 'en', 'ar', 'value']) {
      const resolved = readString(value[key]);
      if (resolved) {
        return resolved;
      }
    }

    return '';
  }

  return String(value).trim();
};

const readBool = (value, fallback = false) => {
  if (value == null) {
    return fallback;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) {
    return true;
  }
  if (['false', '0', 'no', 'n', ''].includes(normalized)) {
    return false;
  }

  return fallback;
};

const readNumberString = (value) => {
  if (value == null || value === '') {
    return '';
  }

  const parsed = Number(value);
  if (Number.isNaN(parsed) || parsed === 0) {
    return '';
  }

  return Number.isInteger(parsed) ? String(parsed) : String(parsed);
};

const isImageFile = (file) => {
  if (!file) {
    return false;
  }

  if (file.type?.startsWith('image/')) {
    return true;
  }

  return /\.(avif|bmp|gif|jpe?g|png|webp)$/i.test(file.name || '');
};

const revokeObjectUrl = (url) => {
  if (
    typeof URL !== 'undefined' &&
    typeof URL.revokeObjectURL === 'function' &&
    typeof url === 'string' &&
    url.startsWith('blob:')
  ) {
    URL.revokeObjectURL(url);
  }
};

const readWrappedData = (raw) => {
  const payload = readMap(raw);
  const wrappedData = readMap(payload.data);
  return Object.keys(wrappedData).length > 0 ? wrappedData : payload;
};

const extractOptionNames = (raw) => {
  const data = readWrappedData(raw);
  const source = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];

  return source
    .map((item) => readString(readMap(item).name || item))
    .filter(Boolean);
};

const extractNamedItems = (raw) =>
  readList(raw)
    .map((item) => readString(readMap(item).name || item))
    .filter(Boolean);

const normalizeChoice = (value, options, fallback) =>
  options.includes(value) ? value : fallback;

const mergeUniqueItems = (items, nextValue) => {
  const normalizedValue = readString(nextValue);
  if (!normalizedValue) {
    return items;
  }

  const exists = items.some(
    (item) => item.trim().toLowerCase() === normalizedValue.trim().toLowerCase()
  );
  if (exists) {
    return items;
  }

  return [...items, normalizedValue];
};

const splitFullName = (name) => {
  const parts = readString(name).split(/\s+/).filter(Boolean);

  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
};

const composeFullName = (formData) =>
  [formData.firstName, formData.lastName]
    .map(readString)
    .filter(Boolean)
    .join(' ') || readString(formData.name);

const getMemberNameParts = (member) => {
  const fallback = splitFullName(member?.name);

  return {
    firstName: readString(member?.first_name || member?.firstName) || fallback.firstName,
    lastName: readString(member?.last_name || member?.lastName) || fallback.lastName,
  };
};

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const calculateAge = (dateOfBirth) => {
  const date = parseDateValue(dateOfBirth);
  if (!date) {
    return null;
  }

  const today = new Date();
  let years = today.getFullYear() - date.getFullYear();
  const hasBirthdayPassed =
    today.getMonth() > date.getMonth() ||
    (today.getMonth() === date.getMonth() && today.getDate() >= date.getDate());

  if (!hasBirthdayPassed) {
    years -= 1;
  }

  return years > 0 ? years : null;
};

const getPatientInitials = (member) => {
  const source = readString(member?.name) || readString(member?.firstName) || 'P';
  const parts = source.split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
  return initials || 'P';
};

const formatNumberValue = (value) => {
  const normalized = readNumberString(value);
  return normalized || '';
};

const sectionKeys = [
  'chronicDiseases',
  'permanentMedications',
  'allergies',
  'surgeries',
  'vaccines',
];

const SavedOptionsDialog = ({
  open,
  onOpenChange,
  title,
  options,
  selectedItems,
  onSelect,
  labels,
}) => {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  const filteredOptions = useMemo(() => {
    const selected = new Set(selectedItems.map((item) => item.trim().toLowerCase()));
    return options.filter((option) => {
      const normalized = option.trim().toLowerCase();
      if (!normalized || selected.has(normalized)) {
        return false;
      }

      if (!query.trim()) {
        return true;
      }

      return normalized.includes(query.trim().toLowerCase());
    });
  }, [options, query, selectedItems]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={labels.searchSavedOptions}
          />

          <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
            {filteredOptions.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                {labels.noSavedOptionsFound}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  className="flex w-full items-center justify-between rounded-2xl border border-slate-200 px-4 py-3 text-left text-sm font-medium text-slate-700 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 dark:border-slate-700 dark:text-slate-200 dark:hover:border-indigo-500 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-300"
                  onClick={() => {
                    onSelect(option);
                    onOpenChange(false);
                  }}
                >
                  <span>{option}</span>
                  <Plus className="h-4 w-4" />
                </button>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MedicalItemsField = ({
  title,
  hint,
  items,
  inputValue,
  onInputChange,
  onAdd,
  onRemove,
  onChooseSuggestions,
  hasSuggestions,
  labels,
}) => (
  <div className="rounded-[28px] border border-slate-200 bg-slate-50/60 p-4 md:p-5 dark:border-slate-700 dark:bg-slate-800/70">
    <div className="mb-3 flex items-center justify-between gap-3">
      <div>
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {items.length > 0 ? `${items.length} ${labels.itemsSelected}` : labels.noDataAvailable}
        </p>
      </div>
    </div>

    {items.length > 0 ? (
      <div className="mb-4 flex flex-wrap gap-2">
        {items.map((item) => (
          <Badge
            key={item}
            variant="outline"
            className="rounded-full border border-indigo-200 bg-white px-3 py-1 text-sm text-indigo-700 dark:border-indigo-800 dark:bg-slate-900 dark:text-indigo-300"
          >
            <span>{item}</span>
            <button
              type="button"
              className="ml-2 inline-flex items-center text-slate-400 transition hover:text-rose-600 dark:text-slate-500 dark:hover:text-rose-400"
              onClick={() => onRemove(item)}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </Badge>
        ))}
      </div>
    ) : null}

    <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
      <Input
        value={inputValue}
        onChange={(event) => onInputChange(event.target.value)}
        placeholder={hint}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            onAdd();
          }
        }}
      />
      <Button type="button" className="bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400" onClick={onAdd}>
        <Plus className="mr-2 h-4 w-4" />
        {labels.add}
      </Button>
    </div>

    <Button
      type="button"
      variant="outline"
      className="mt-3 w-full justify-center rounded-2xl border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-200"
      onClick={onChooseSuggestions}
      disabled={!hasSuggestions}
    >
      <ListPlus className="mr-2 h-4 w-4" />
      {labels.chooseFromSavedOptions}
    </Button>
  </div>
);

const withImageCacheVersion = (imageUrl, version) => {
  if (
    !imageUrl ||
    !version ||
    imageUrl.startsWith('data:image') ||
    imageUrl.startsWith('blob:')
  ) {
    return imageUrl;
  }

  const separator = imageUrl.includes('?') ? '&' : '?';
  return `${imageUrl}${separator}v=${encodeURIComponent(version)}`;
};

const PatientAvatar = ({ member, size = 'md' }) => {
  const isLarge = size === 'lg';
  const isFemale = readString(member?.gender).toLowerCase() === 'female';
  const imageSrc = withImageCacheVersion(member?.imageUrl || '', member?.imageVersion);
  const fallbackClass = isFemale
    ? 'bg-rose-100 text-rose-600 dark:bg-rose-950/50 dark:text-rose-200'
    : 'bg-teal-100 text-teal-700 dark:bg-teal-950/50 dark:text-teal-200';

  return (
    <Avatar
      className={
        isLarge
          ? 'h-24 w-24 border-4 border-white shadow-lg shadow-slate-200/70 dark:border-slate-800 dark:shadow-slate-950/50'
          : 'h-16 w-16 border-4 border-white shadow-md shadow-slate-200/70 dark:border-slate-800 dark:shadow-slate-950/40'
      }
    >
      <AvatarImage
        src={imageSrc || undefined}
        alt={readString(member?.name) || 'Patient'}
        className="object-cover"
      />
      <AvatarFallback className={`${fallbackClass} text-2xl font-bold`}>
        {getPatientInitials(member)}
      </AvatarFallback>
    </Avatar>
  );
};

const PatientMetaItem = ({ icon: Icon, label, value }) => (
  <span className="inline-flex min-h-9 items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-300">
    <Icon className="h-4 w-4 text-slate-400" />
    <span className="text-xs font-medium text-slate-400 dark:text-slate-500">{label}</span>
    <span className="font-semibold text-slate-700 dark:text-slate-200">{value}</span>
  </span>
);

const FormSection = ({ icon: Icon, title, children }) => (
  <section className="rounded-[26px] border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-800/50">
    <div className="mb-4 flex items-center gap-2">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-white text-indigo-600 shadow-sm dark:bg-slate-900 dark:text-indigo-300">
        <Icon className="h-4 w-4" />
      </span>
      <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
    </div>
    {children}
  </section>
);

const FamilyMembersPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language?.startsWith('ar') ? 'ar' : 'en';
  const { isAuthenticated, user, logout } = useAuth();
  const {
    familyMembers,
    loading,
    addFamilyMember,
    updateFamilyMember,
    removeFamilyMember,
    setDefaultMember,
  } = useFamily();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(createEmptyForm());
  const [tagInputs, setTagInputs] = useState(createEmptyTagInputs());
  const [verificationStep, setVerificationStep] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [medicalOptions, setMedicalOptions] = useState(createEmptyMedicalOptions());
  const [isMedicalOptionsLoading, setIsMedicalOptionsLoading] = useState(false);
  const [isMedicalStateLoading, setIsMedicalStateLoading] = useState(false);
  const [optionsPicker, setOptionsPicker] = useState(null);
  const patientImageInputRef = useRef(null);

  useEffect(() => {
    const previewUrl = formData.imagePreviewUrl;
    return () => {
      revokeObjectUrl(previewUrl);
    };
  }, [formData.imagePreviewUrl]);

  const labels = useMemo(
    () => ({
      pageTitle: lang === 'ar' ? 'المرضى' : 'Patients',
      pageSubtitle:
        lang === 'ar'
          ? 'إدارة المرضى المرتبطين بهذا الحساب'
          : 'Manage patient profiles linked to this account',
      back: lang === 'ar' ? 'رجوع' : 'Back',
      loginTitle: lang === 'ar' ? 'يرجى تسجيل الدخول' : 'Please Login',
      loginBody:
        lang === 'ar'
          ? 'تحتاج إلى تسجيل الدخول لإدارة المرضى'
          : 'You need to login to manage patients',
      loginButton: lang === 'ar' ? 'تسجيل الدخول' : 'Login',
      addPatient: lang === 'ar' ? 'إضافة مريض' : 'Add Patient',
      editPatient: lang === 'ar' ? 'تعديل المريض' : 'Edit Patient',
      createPatientProfile: lang === 'ar' ? 'إنشاء ملف مريض' : 'Create Patient Profile',
      editPatientProfile: lang === 'ar' ? 'تعديل ملف المريض' : 'Edit Patient Profile',
      save: lang === 'ar' ? 'حفظ' : 'Save',
      saveChanges: lang === 'ar' ? 'حفظ التغييرات' : 'Save Changes',
      saving: lang === 'ar' ? 'جارٍ الحفظ...' : 'Saving...',
      loading: lang === 'ar' ? 'جارٍ التحميل...' : 'Loading...',
      cancel: lang === 'ar' ? 'إلغاء' : 'Cancel',
      delete: lang === 'ar' ? 'حذف' : 'Delete',
      edit: lang === 'ar' ? 'تعديل' : 'Edit',
      defaultPatient: lang === 'ar' ? 'الافتراضي' : 'Default',
      setDefault: lang === 'ar' ? 'تعيين كافتراضي' : 'Set as default',
      noPatientsTitle: lang === 'ar' ? 'لا يوجد مرضى بعد' : 'No patient profiles yet',
      noPatientsBody:
        lang === 'ar'
          ? 'أضف مريضًا جديدًا إلى هذا الحساب للمتابعة.'
          : 'Add a new patient profile to this account to continue.',
      helper:
        lang === 'ar'
          ? 'يمكنك استخدام هؤلاء المرضى أثناء الحجز، مع إمكانية إضافة أو تعديل أو حذف أي مريض.'
          : 'You can use these patients while booking, with full add, edit, and delete support.',
      patientInformation: lang === 'ar' ? 'معلومات المريض' : 'Patient Information',
      medicalData: lang === 'ar' ? 'البيانات الطبية' : 'Medical Data',
      lifestyle: lang === 'ar' ? 'نمط الحياة' : 'Lifestyle',
      medicalHistory: lang === 'ar' ? 'السجل الطبي' : 'Medical History',
      profileOverview: lang === 'ar' ? 'ملخص المريض' : 'Patient Summary',
      addPatientImage: lang === 'ar' ? 'إضافة صورة المريض' : 'Add patient image',
      changePatientImage: lang === 'ar' ? 'تغيير صورة المريض' : 'Change patient image',
      clearSelectedImage: lang === 'ar' ? 'إلغاء الصورة المختارة' : 'Clear selected image',
      fullName: lang === 'ar' ? 'الاسم الكامل' : 'Full Name',
      firstName: lang === 'ar' ? 'الاسم الأول' : 'First Name',
      lastName: lang === 'ar' ? 'اسم العائلة' : 'Last Name',
      phone: lang === 'ar' ? 'رقم الهاتف' : 'Phone',
      email: lang === 'ar' ? 'البريد الإلكتروني' : 'Email',
      gender: lang === 'ar' ? 'الجنس' : 'Gender',
      dateOfBirth: lang === 'ar' ? 'تاريخ الميلاد' : 'Date of Birth',
      bloodGroup: lang === 'ar' ? 'فصيلة الدم' : 'Blood Group',
      weight: lang === 'ar' ? 'الوزن (كغ)' : 'Weight (kg)',
      height: lang === 'ar' ? 'الطول (سم)' : 'Height (cm)',
      smoker: lang === 'ar' ? 'مدخن' : 'Smoker',
      smokingYears: lang === 'ar' ? 'سنوات التدخين' : 'Smoking Years',
      alcohol: lang === 'ar' ? 'الكحول' : 'Alcohol',
      exercise: lang === 'ar' ? 'الرياضة' : 'Exercise',
      note: lang === 'ar' ? 'ملاحظة' : 'Note',
      chronicDiseases: lang === 'ar' ? 'الأمراض المزمنة' : 'Chronic disease',
      permanentMedications:
        lang === 'ar' ? 'الأدوية الدائمة' : 'Permanent medication',
      allergies: lang === 'ar' ? 'حساسيات الأدوية' : 'Drug allergies',
      surgeries: lang === 'ar' ? 'العمليات الجراحية' : 'Surgical operations',
      vaccines: lang === 'ar' ? 'اللقاحات' : 'Vaccines',
      addChronicDisease: lang === 'ar' ? 'أضف مرضًا مزمنًا' : 'Add chronic disease',
      addPermanentMedication:
        lang === 'ar' ? 'أضف دواءً دائمًا' : 'Add permanent medication',
      addAllergy: lang === 'ar' ? 'أضف حساسية دوائية' : 'Add drug allergy',
      addSurgery: lang === 'ar' ? 'أضف عملية جراحية' : 'Add surgery',
      addVaccine: lang === 'ar' ? 'أضف لقاحًا' : 'Add vaccine',
      chooseFromSavedOptions:
        lang === 'ar' ? 'اختيار من القائمة الجاهزة' : 'Choose from saved options',
      searchSavedOptions:
        lang === 'ar' ? 'ابحث في الخيارات المحفوظة' : 'Search saved options',
      noSavedOptionsFound:
        lang === 'ar' ? 'لا توجد خيارات مطابقة' : 'No matching saved options',
      noDataAvailable: lang === 'ar' ? 'لا توجد بيانات' : 'No data available',
      itemsSelected: lang === 'ar' ? 'عنصر محدد' : 'item(s) selected',
      add: lang === 'ar' ? 'إضافة' : 'Add',
      moreOptions: lang === 'ar' ? 'خيارات إضافية' : 'More options',
      age: lang === 'ar' ? 'العمر' : 'Age',
      years: lang === 'ar' ? 'سنة' : 'years',
      kg: lang === 'ar' ? 'كغ' : 'kg',
      noValue: '--',
      deleteConfirm:
        lang === 'ar'
          ? 'هل أنت متأكد من حذف هذا المريض؟'
          : 'Are you sure you want to delete this patient?',
      requiredError:
        lang === 'ar'
          ? 'يرجى تعبئة الاسم ورقم الهاتف والجنس وتاريخ الميلاد'
          : 'Please fill name, phone, gender, and date of birth',
      duplicatePhoneError:
        lang === 'ar'
          ? 'رقم الهاتف مستخدم لمريض آخر. يمكن تكرار رقم الحساب الأساسي فقط داخل الحساب.'
          : 'This phone number is already used by another patient. Only the primary account phone can be reused within this account.',
      saveSuccessCreate:
        lang === 'ar' ? 'تمت إضافة المريض بنجاح' : 'Patient added successfully',
      saveSuccessUpdate:
        lang === 'ar' ? 'تم تحديث بيانات المريض' : 'Patient updated successfully',
      deleteSuccess: lang === 'ar' ? 'تم حذف المريض' : 'Patient deleted successfully',
      accountDeletedSuccess:
        lang === 'ar' ? 'تم حذف الحساب بنجاح' : 'Account deleted successfully',
      defaultSuccess:
        lang === 'ar' ? 'تم تعيين المريض كافتراضي' : 'Patient set as default',
      defaultConflict:
        lang === 'ar'
          ? 'يوجد مريض افتراضي آخر محدد بالفعل: {name}. الرجاء إلغاء الافتراضي الحالي أولاً.'
          : 'Another default patient is already assigned: {name}. Please remove the current default first.',
      verifyPhoneTitle:
        lang === 'ar' ? 'تأكيد رقم هاتف المريض' : 'Verify Patient Phone Number',
      verifyPhoneBody:
        lang === 'ar'
          ? 'تم تغيير رقم الهاتف. يجب التحقق قبل حفظ ملف المريض.'
          : 'Phone number changed. Verification is required before saving the patient profile.',
      otpSent:
        lang === 'ar' ? 'تم إرسال رمز التحقق بنجاح' : 'Verification code sent successfully',
      otpCode: lang === 'ar' ? 'رمز التحقق' : 'OTP Code',
      otpHint:
        lang === 'ar'
          ? 'أدخل الرمز المرسل إلى هذا الرقم.'
          : 'Enter the code sent to this phone number.',
      verify: lang === 'ar' ? 'تحقق' : 'Verify',
      resendOtp: lang === 'ar' ? 'إعادة إرسال الرمز' : 'Resend OTP',
      phoneVerified:
        lang === 'ar' ? 'تم تأكيد رقم الهاتف بنجاح' : 'Phone verified successfully',
      otpRequired:
        lang === 'ar' ? 'يرجى إدخال رمز التحقق' : 'Please enter OTP code',
      otpTooShort:
        lang === 'ar' ? 'رمز التحقق قصير جدًا' : 'OTP code is too short',
      genericSaveError:
        lang === 'ar' ? 'فشل في حفظ بيانات المريض' : 'Failed to save patient profile',
      imageTypeError:
        lang === 'ar' ? 'يرجى اختيار ملف صورة صالح' : 'Please choose a valid image file',
      genericDeleteError:
        lang === 'ar' ? 'فشل في حذف المريض' : 'Failed to delete patient',
      loadingMedical:
        lang === 'ar' ? 'جارٍ تحميل البيانات الطبية...' : 'Loading medical data...',
      loadingSavedOptions:
        lang === 'ar' ? 'جارٍ تحميل الخيارات المحفوظة...' : 'Loading saved options...',
      male: lang === 'ar' ? 'ذكر' : 'Male',
      female: lang === 'ar' ? 'أنثى' : 'Female',
      none: lang === 'ar' ? 'لا يوجد' : 'None',
      occasional: lang === 'ar' ? 'أحيانًا' : 'Occasional',
      moderate: lang === 'ar' ? 'متوسط' : 'Moderate',
      heavy: lang === 'ar' ? 'كثير' : 'Heavy',
      rarely: lang === 'ar' ? 'نادراً' : 'Rarely',
      weekly: lang === 'ar' ? 'أسبوعيًا' : 'Weekly',
      daily: lang === 'ar' ? 'يوميًا' : 'Daily',
    }),
    [lang]
  );

  const medicalSections = useMemo(
    () => [
      {
        key: 'chronicDiseases',
        title: labels.chronicDiseases,
        hint: labels.addChronicDisease,
      },
      {
        key: 'permanentMedications',
        title: labels.permanentMedications,
        hint: labels.addPermanentMedication,
      },
      {
        key: 'allergies',
        title: labels.allergies,
        hint: labels.addAllergy,
      },
      {
        key: 'surgeries',
        title: labels.surgeries,
        hint: labels.addSurgery,
      },
      {
        key: 'vaccines',
        title: labels.vaccines,
        hint: labels.addVaccine,
      },
    ],
    [labels]
  );

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 dark:bg-slate-950">
        <Card className="w-full max-w-md border-0 shadow-lg shadow-slate-200/60 dark:bg-slate-900 dark:shadow-slate-950/40">
          <CardContent className="p-8 text-center">
            <Users className="mx-auto mb-4 h-14 w-14 text-slate-300 dark:text-slate-600" />
            <h2 className="mb-2 text-xl font-semibold text-slate-900 dark:text-slate-100">{labels.loginTitle}</h2>
            <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">{labels.loginBody}</p>
            <Button onClick={() => navigate('/login')}>{labels.loginButton}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const resetDialogState = () => {
    setIsDialogOpen(false);
    setEditingMember(null);
    setFormData(createEmptyForm());
    setTagInputs(createEmptyTagInputs());
    setVerificationStep(null);
    setOtpCode('');
    setOtpError('');
    setOptionsPicker(null);
    setIsMedicalStateLoading(false);
  };

  const loadMedicalOptions = async () => {
    if (isMedicalOptionsLoading) {
      return;
    }

    setIsMedicalOptionsLoading(true);
    try {
      const response = await medicalRecordService.getHistoryOptions();
      const data = readWrappedData(response?.data);
      const nextOptions = {
        chronicDiseases: extractOptionNames(data.chronic_diseases),
        permanentMedications: extractOptionNames(data.permanent_medications),
        allergies: extractOptionNames(data.allergies),
        surgeries: extractOptionNames(data.surgeries),
        vaccines: extractOptionNames(data.vaccines),
      };

      if (sectionKeys.some((key) => nextOptions[key].length > 0)) {
        setMedicalOptions(nextOptions);
        return;
      }
    } catch (_) {}

    try {
      const responses = await Promise.all([
        medicalRecordService.getChronicDiseases(),
        medicalRecordService.getMedications(),
        medicalRecordService.getAllergies(),
        medicalRecordService.getSurgeries(),
        medicalRecordService.getVaccines(),
      ]);

      setMedicalOptions({
        chronicDiseases: extractOptionNames(responses[0]?.data),
        permanentMedications: extractOptionNames(responses[1]?.data),
        allergies: extractOptionNames(responses[2]?.data),
        surgeries: extractOptionNames(responses[3]?.data),
        vaccines: extractOptionNames(responses[4]?.data),
      });
    } catch (error) {
      console.error('Failed to load medical options:', error);
      setMedicalOptions(createEmptyMedicalOptions());
    } finally {
      setIsMedicalOptionsLoading(false);
    }
  };

  const hydrateMedicalDetails = async (member) => {
    if (!member) {
      return;
    }

    setIsMedicalStateLoading(true);
    try {
      let response;

      try {
        response = await medicalRecordService.getPatientMedicalRecord(
          member.numericId || member.id
        );
      } catch (error) {
        if (
          !(member.isDefault || member.isSelf) ||
          !shouldFallbackToAccountProfile(error)
        ) {
          throw error;
        }

        response = await medicalRecordService.getHistory();
      }

      const data = readWrappedData(response?.data);
      const patient = readMap(data.patient);
      const medicalHistory = readMap(data.medical_history);

      setFormData((prev) => ({
        ...prev,
        bloodGroup: readString(patient.blood_type) || prev.bloodGroup,
        height: readNumberString(patient.height) || prev.height,
        weight: readNumberString(patient.weight) || prev.weight,
        smoker: readBool(medicalHistory.is_smoker),
        smokingYears: readNumberString(medicalHistory.smoking_years),
        alcoholConsumption: normalizeChoice(
          readString(medicalHistory.alcohol_consumption),
          alcoholOptions,
          'none'
        ),
        exerciseFrequency: normalizeChoice(
          readString(medicalHistory.exercise_frequency),
          exerciseOptions,
          'none'
        ),
        familyHistory: readString(medicalHistory.family_history),
        chronicDiseases: extractNamedItems(medicalHistory.chronic_diseases),
        permanentMedications: extractNamedItems(medicalHistory.permanent_medications),
        allergies: extractNamedItems(medicalHistory.allergies),
        surgeries: extractNamedItems(medicalHistory.surgeries),
        vaccines: extractNamedItems(medicalHistory.vaccines),
      }));
    } catch (error) {
      console.error('Failed to load member medical details:', error);
    } finally {
      setIsMedicalStateLoading(false);
    }
  };

  const openCreateDialog = async () => {
    setEditingMember(null);
    setVerificationStep(null);
    setOtpCode('');
    setOtpError('');
    setTagInputs(createEmptyTagInputs());
    setFormData({
      ...createEmptyForm(),
      phone: getAccountPhone(),
      isDefault: familyMembers.length === 0,
    });
    setIsDialogOpen(true);
    setIsMedicalStateLoading(false);
    await loadMedicalOptions();
  };

  const openEditDialog = async (member) => {
    const nameParts = getMemberNameParts(member);
    setEditingMember(member);
    setVerificationStep(null);
    setOtpCode('');
    setOtpError('');
    setTagInputs(createEmptyTagInputs());
    setFormData({
      ...createEmptyForm(),
      name: member.name || '',
      firstName: nameParts.firstName,
      lastName: nameParts.lastName,
      phone: member.phone || '',
      email: member.email || '',
      gender: member.gender || '',
      dateOfBirth: member.dateOfBirth || '',
      bloodGroup: member.bloodGroup || '',
      weight: member.weight || '',
      height: member.height || '',
      smoker: !!member.smoker,
      smokingYears: member.smokingYears || '',
      alcoholConsumption: normalizeChoice(
        member.alcoholConsumption || 'none',
        alcoholOptions,
        'none'
      ),
      exerciseFrequency: normalizeChoice(
        member.exerciseFrequency || 'none',
        exerciseOptions,
        'none'
      ),
      familyHistory: member.familyHistory || '',
      chronicDiseases: Array.isArray(member.chronicDiseases) ? member.chronicDiseases : [],
      permanentMedications: Array.isArray(member.permanentMedications)
        ? member.permanentMedications
        : [],
      allergies: Array.isArray(member.allergies) ? member.allergies : [],
      surgeries: Array.isArray(member.surgeries) ? member.surgeries : [],
      vaccines: Array.isArray(member.vaccines) ? member.vaccines : [],
      isDefault: !!member.isDefault,
    });
    setIsDialogOpen(true);
    await Promise.all([loadMedicalOptions(), hydrateMedicalDetails(member)]);
  };

  const getAccountPhone = () => {
    const selfMemberPhone = normalizePhone(
      familyMembers.find((member) => member.isSelf)?.phone || ''
    );
    if (selfMemberPhone) {
      return selfMemberPhone;
    }

    const defaultMemberPhone = normalizePhone(
      familyMembers.find((member) => member.isDefault)?.phone || ''
    );
    if (defaultMemberPhone) {
      return defaultMemberPhone;
    }

    const patient = readMap(user?.patient);
    const patientPhone = normalizePhone(
      patient.phone || patient.phone_number || patient.mobile || patient.mobile_number || ''
    );
    if (patientPhone) {
      return patientPhone;
    }

    return normalizePhone(
      user?.phone || user?.phone_number || user?.mobile || user?.mobile_number || ''
    );
  };

  const requiresPhoneVerification = (member, nextPhone) => {
    const normalizedPhone = normalizePhone(nextPhone);
    const normalizedAccountPhone = getAccountPhone();
    const normalizedExistingPhone = normalizePhone(member?.phone || '');

    if (!normalizedPhone) {
      return false;
    }

    if (normalizedAccountPhone && normalizedPhone === normalizedAccountPhone) {
      return false;
    }

    if (member && normalizedExistingPhone && normalizedPhone === normalizedExistingPhone) {
      return false;
    }

    return true;
  };

  const isDuplicatePatientPhone = (member, nextPhone) => {
    const normalizedPhone = normalizePhone(nextPhone);
    const normalizedAccountPhone = getAccountPhone();

    if (!normalizedPhone) {
      return false;
    }

    if (normalizedAccountPhone && normalizedPhone === normalizedAccountPhone) {
      return false;
    }

    return familyMembers.some((patient) => {
      if (member && String(patient.id) === String(member.id)) {
        return false;
      }

      return normalizePhone(patient.phone) === normalizedPhone;
    });
  };

  const buildMemberDataForSave = () => ({
    ...formData,
    name: composeFullName(formData),
    phone: readString(formData.phone),
    email: readString(formData.email),
  });

  const findAssignedDefaultPatient = (excludingMember = null) =>
    familyMembers.find((patient) => {
      if (!patient.isDefault) {
        return false;
      }

      return String(patient.id) !== String(excludingMember?.id || '');
    }) || null;

  const showDefaultPatientConflictMessage = (defaultPatient) => {
    const defaultPatientName = readString(defaultPatient?.name) || labels.defaultPatient;
    toast.error(labels.defaultConflict.replace('{name}', defaultPatientName));
  };

  const canAssignDefaultPatient = (excludingMember = null) => {
    const currentDefault = findAssignedDefaultPatient(excludingMember);
    if (!currentDefault) {
      return true;
    }

    showDefaultPatientConflictMessage(currentDefault);
    return false;
  };

  const saveMember = async (memberData, phoneVerificationToken = null) => {
    if (editingMember) {
      await updateFamilyMember(
        editingMember.id,
        { ...memberData, phoneVerificationToken },
        { phoneVerificationToken }
      );
      toast.success(labels.saveSuccessUpdate);
    } else {
      await addFamilyMember({ ...memberData, phoneVerificationToken });
      toast.success(labels.saveSuccessCreate);
    }
  };

  const requestPhoneVerification = async (memberData) => {
    const phone = memberData.phone;
    const normalizedPhone = normalizePhone(phone);
    await authService.requestPatientPhoneVerification(normalizedPhone);
    toast.success(labels.otpSent);
    setOtpCode('');
    setOtpError('');
    setVerificationStep({
      phone: normalizedPhone,
      memberData,
    });
  };

  const handleSubmit = async () => {
    const memberData = buildMemberDataForSave();

    if (!memberData.name || !memberData.phone || !memberData.gender || !memberData.dateOfBirth) {
      toast.error(labels.requiredError);
      return;
    }

    if (isDuplicatePatientPhone(editingMember, memberData.phone)) {
      toast.error(labels.duplicatePhoneError);
      return;
    }

    if (memberData.isDefault && !canAssignDefaultPatient(editingMember)) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (requiresPhoneVerification(editingMember, memberData.phone)) {
        await requestPhoneVerification(memberData);
        return;
      }

      await saveMember(memberData);
      resetDialogState();
    } catch (error) {
      console.error('Failed to save patient:', error);
      toast.error(error?.response?.data?.message || error?.message || labels.genericSaveError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!verificationStep) {
      return;
    }

    if (!otpCode.trim()) {
      setOtpError(labels.otpRequired);
      return;
    }

    if (otpCode.trim().length < 4) {
      setOtpError(labels.otpTooShort);
      return;
    }

    if (
      verificationStep.memberData?.isDefault &&
      !canAssignDefaultPatient(editingMember)
    ) {
      return;
    }

    setIsSubmitting(true);
    setOtpError('');
    try {
      const response = await authService.verifyPatientPhoneVerification(
        verificationStep.phone,
        otpCode.trim()
      );
      const verificationToken = extractVerificationToken(response);
      if (!verificationToken) {
        throw new Error(labels.genericSaveError);
      }

      toast.success(labels.phoneVerified);
      await saveMember(verificationStep.memberData, verificationToken);
      resetDialogState();
    } catch (error) {
      setOtpError(error?.response?.data?.message || error?.message || labels.genericSaveError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (!verificationStep) {
      return;
    }

    setIsResendingOtp(true);
    setOtpError('');
    try {
      await authService.requestPatientPhoneVerification(verificationStep.phone);
      toast.success(labels.otpSent);
    } catch (error) {
      setOtpError(error?.response?.data?.message || error?.message || labels.genericSaveError);
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleDialogChange = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      resetDialogState();
    }
  };

  const handleChoosePatientImage = () => {
    if (isSubmitting) {
      return;
    }

    patientImageInputRef.current?.click();
  };

  const handlePatientImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!isImageFile(file)) {
      toast.error(labels.imageTypeError);
      return;
    }

    const previewUrl =
      typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
        ? URL.createObjectURL(file)
        : '';

    setFormData((prev) => ({
      ...prev,
      imageFile: file,
      imagePreviewUrl: previewUrl,
    }));
  };

  const clearSelectedPatientImage = () => {
    setFormData((prev) => ({
      ...prev,
      imageFile: null,
      imagePreviewUrl: '',
    }));
  };

  const handleDelete = async (member) => {
    if (!window.confirm(labels.deleteConfirm)) {
      return;
    }

    try {
      const result = await removeFamilyMember(member.id);
      if (result?.accountDeleted) {
        toast.success(result.message || labels.accountDeletedSuccess);
        await logout();
        navigate('/');
        return;
      }

      toast.success(result?.message || labels.deleteSuccess);
    } catch (error) {
      console.error('Failed to delete patient:', error);
      toast.error(error?.response?.data?.message || error?.message || labels.genericDeleteError);
    }
  };

  const handleSetDefault = async (member) => {
    if (!canAssignDefaultPatient(member)) {
      return;
    }

    try {
      await setDefaultMember(member.id);
      toast.success(labels.defaultSuccess);
    } catch (error) {
      console.error('Failed to set default patient:', error);
      toast.error(error?.response?.data?.message || error?.message || labels.genericSaveError);
    }
  };

  const setFieldValue = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const setTagInputValue = (key, value) => {
    setTagInputs((prev) => ({ ...prev, [key]: value }));
  };

  const addMedicalItem = (key, rawValue = tagInputs[key]) => {
    setFormData((prev) => ({
      ...prev,
      [key]: mergeUniqueItems(prev[key], rawValue),
    }));
    setTagInputValue(key, '');
  };

  const removeMedicalItem = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: prev[key].filter((item) => item !== value),
    }));
  };

  const renderList = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-24 text-slate-500 dark:text-slate-400">
          <Loader2 className="mr-3 h-5 w-5 animate-spin" />
          {labels.loading}
        </div>
      );
    }

    if (familyMembers.length === 0) {
      return (
        <Card className="border border-dashed border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <CardContent className="px-6 py-14 text-center">
            <Users className="mx-auto mb-4 h-12 w-12 text-slate-300 dark:text-slate-600" />
            <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">{labels.noPatientsTitle}</h3>
            <p className="mx-auto mb-6 max-w-md text-sm text-slate-500 dark:text-slate-400">{labels.noPatientsBody}</p>
            <Button onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              {labels.addPatient}
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-5">
        {familyMembers.map((member) => {
          const age = calculateAge(member.dateOfBirth);
          const ageText = age ? `${age} ${labels.years}` : labels.noValue;
          const weight = formatNumberValue(member.weight);
          const weightText = weight ? `${weight} ${labels.kg}` : labels.noValue;
          const bloodGroup = readString(member.bloodGroup) || labels.noValue;
          const displayDate = readString(member.dateOfBirth) || labels.noValue;

          return (
            <Card
              key={member.id}
              className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)] transition hover:-translate-y-0.5 hover:shadow-[0_22px_55px_rgba(15,23,42,0.11)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_18px_45px_rgba(2,6,23,0.45)]"
            >
              <CardContent className="p-5 sm:p-6">
                <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-start gap-4 rounded-2xl text-left outline-none transition focus-visible:ring-2 focus-visible:ring-indigo-500"
                    onClick={() => openEditDialog(member)}
                  >
                    <PatientAvatar member={member} />

                    <span className="block min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="break-words text-xl font-semibold text-slate-950 dark:text-slate-50">
                          {member.name || labels.fullName}
                        </span>
                        {member.isDefault ? (
                          <span className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-300">
                            <Star className="mr-1 h-3.5 w-3.5" />
                            {labels.defaultPatient}
                          </span>
                        ) : null}
                      </span>

                      <span className="mt-2 flex flex-wrap gap-3 text-sm text-slate-500 dark:text-slate-400">
                        {member.phone ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Phone className="h-3.5 w-3.5" />
                            {member.phone}
                          </span>
                        ) : null}
                        {member.email ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5" />
                            {member.email}
                          </span>
                        ) : null}
                        {member.dateOfBirth ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5" />
                            {member.dateOfBirth}
                          </span>
                        ) : null}
                      </span>

                      <span className="mt-4 flex flex-wrap gap-2">
                        <PatientMetaItem icon={Cake} label={labels.age} value={ageText} />
                        <PatientMetaItem icon={Scale} label={labels.weight} value={weightText} />
                        <PatientMetaItem icon={Droplets} label={labels.bloodGroup} value={bloodGroup} />
                        <PatientMetaItem icon={Calendar} label={labels.dateOfBirth} value={displayDate} />
                      </span>
                    </span>
                  </button>

                  <div className="flex flex-wrap gap-3 md:max-w-[320px] md:justify-end">
                    <Button
                      variant="outline"
                      className="h-11 rounded-2xl border-indigo-300 px-5 text-indigo-600 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
                      onClick={() => openEditDialog(member)}
                    >
                      <Edit2 className="mr-2 h-4 w-4" />
                      {labels.edit}
                    </Button>

                    <Button
                      variant="outline"
                      className="h-11 rounded-2xl border-rose-300 px-5 text-rose-600 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-950/30"
                      onClick={() => handleDelete(member)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {labels.delete}
                    </Button>

                    {!member.isDefault ? (
                      <Button
                        variant="ghost"
                        className="h-11 rounded-2xl px-5 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-200"
                        onClick={() => handleSetDefault(member)}
                      >
                        <Star className="mr-2 h-4 w-4" />
                        {labels.setDefault}
                      </Button>
                    ) : null}

                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          className="mb-6 rounded-full px-3 text-slate-600 hover:bg-white dark:text-slate-300 dark:hover:bg-slate-900"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          {labels.back}
        </Button>

        <Card className="mb-8 overflow-hidden border-0 bg-gradient-to-r from-slate-950 via-indigo-950 to-violet-950 text-white shadow-[0_25px_60px_rgba(15,23,42,0.22)]">
          <CardContent className="flex flex-col gap-5 px-6 py-8 md:flex-row md:items-center md:justify-between md:px-8">
            <div className="flex items-start gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-white/10">
                <Users className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-3xl font-semibold tracking-tight">{labels.pageTitle}</h1>
                <p className="mt-2 max-w-2xl text-sm text-white/70">{labels.pageSubtitle}</p>
                <p className="mt-3 max-w-2xl text-sm text-white/65">{labels.helper}</p>
              </div>
            </div>

            <Button className="h-12 rounded-2xl bg-white px-6 text-base font-semibold text-slate-950 shadow-lg shadow-black/10 hover:bg-slate-100 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-white" onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              {labels.addPatient}
            </Button>
          </CardContent>
        </Card>

        {renderList()}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto rounded-3xl border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
          <DialogHeader>
            <DialogTitle>
              {verificationStep
                ? labels.verifyPhoneTitle
                : editingMember
                  ? labels.editPatientProfile
                  : labels.createPatientProfile}
            </DialogTitle>
          </DialogHeader>

          {verificationStep ? (
            <div className="space-y-5 py-2">
              <p className="text-sm text-slate-600 dark:text-slate-300">{labels.verifyPhoneBody}</p>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                {verificationStep.phone}
              </div>
              <div className="space-y-3">
                <Label>{labels.otpCode}</Label>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otpCode}
                    onChange={(value) => {
                      setOtpCode(value);
                      if (otpError) {
                        setOtpError('');
                      }
                    }}
                    disabled={isSubmitting}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <p className="text-center text-sm text-slate-500 dark:text-slate-400">{labels.otpHint}</p>
                {otpError ? <p className="text-sm text-red-600">{otpError}</p> : null}
              </div>
            </div>
          ) : (
            <div className="space-y-5 py-2">
              <div className="flex flex-col items-center gap-3 rounded-[28px] border border-slate-200 bg-slate-50 px-4 py-5 text-center dark:border-slate-700 dark:bg-slate-800/60">
                <input
                  ref={patientImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePatientImageChange}
                />
                <div className="relative">
                  <PatientAvatar
                    size="lg"
                    member={{
                      ...editingMember,
                      name: composeFullName(formData) || editingMember?.name || labels.fullName,
                      gender: formData.gender,
                      imageUrl: formData.imagePreviewUrl || editingMember?.imageUrl,
                    }}
                  />
                  <button
                    type="button"
                    className="absolute -bottom-1 -right-1 inline-flex h-10 w-10 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg shadow-indigo-200 ring-4 ring-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:shadow-slate-950/40 dark:ring-slate-800 dark:hover:bg-indigo-400"
                    onClick={handleChoosePatientImage}
                    disabled={isSubmitting}
                    aria-label={labels.addPatientImage}
                  >
                    <Camera className="h-4 w-4" />
                  </button>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-950 dark:text-slate-50">
                    {composeFullName(formData) || labels.createPatientProfile}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {editingMember ? labels.editPatientProfile : labels.createPatientProfile}
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-2xl border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
                    onClick={handleChoosePatientImage}
                    disabled={isSubmitting}
                  >
                    <ImagePlus className="mr-2 h-4 w-4" />
                    {formData.imageFile ? labels.changePatientImage : labels.addPatientImage}
                  </Button>
                  {formData.imageFile ? (
                    <Button
                      type="button"
                      variant="ghost"
                      className="rounded-2xl text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                      onClick={clearSelectedPatientImage}
                      disabled={isSubmitting}
                    >
                      <X className="mr-2 h-4 w-4" />
                      {labels.clearSelectedImage}
                    </Button>
                  ) : null}
                </div>
              </div>

              <FormSection icon={UserRound} title={labels.patientInformation}>
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <Label>{labels.firstName}</Label>
                    <Input
                      value={formData.firstName}
                      onChange={(event) => setFieldValue('firstName', event.target.value)}
                      placeholder={labels.firstName}
                    />
                  </div>

                  <div>
                    <Label>{labels.lastName}</Label>
                    <Input
                      value={formData.lastName}
                      onChange={(event) => setFieldValue('lastName', event.target.value)}
                      placeholder={labels.lastName}
                    />
                  </div>

                  <div>
                    <Label>{labels.phone}</Label>
                    <Input
                      value={formData.phone}
                      onChange={(event) => setFieldValue('phone', event.target.value)}
                      placeholder="+963..."
                    />
                  </div>

                  <div>
                    <Label>{labels.email}</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(event) => setFieldValue('email', event.target.value)}
                      placeholder="email@example.com"
                    />
                  </div>

                  <div>
                    <Label>{labels.gender}</Label>
                    <Select value={formData.gender} onValueChange={(value) => setFieldValue('gender', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder={labels.gender} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">{labels.male}</SelectItem>
                        <SelectItem value="female">{labels.female}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{labels.dateOfBirth}</Label>
                    <Input
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(event) => setFieldValue('dateOfBirth', event.target.value)}
                    />
                  </div>

                  <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900/70">
                    <Checkbox
                      id="patient-default"
                      checked={formData.isDefault}
                      onCheckedChange={(checked) => {
                        const shouldAssignDefault = !!checked;
                        if (
                          shouldAssignDefault &&
                          !canAssignDefaultPatient(editingMember)
                        ) {
                          return;
                        }

                        setFieldValue('isDefault', shouldAssignDefault);
                      }}
                    />
                    <Label htmlFor="patient-default" className="cursor-pointer">
                      {labels.setDefault}
                    </Label>
                  </div>
                </div>
              </FormSection>

              <FormSection icon={HeartPulse} title={labels.medicalData}>
                <div className="grid gap-5 md:grid-cols-3">
                  <div>
                    <Label>{labels.bloodGroup}</Label>
                    <Select
                      value={formData.bloodGroup || undefined}
                      onValueChange={(value) => setFieldValue('bloodGroup', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={labels.bloodGroup} />
                      </SelectTrigger>
                      <SelectContent>
                        {bloodGroups.map((group) => (
                          <SelectItem key={group} value={group}>
                            {group}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{labels.height}</Label>
                    <Input
                      type="number"
                      value={formData.height}
                      onChange={(event) => setFieldValue('height', event.target.value)}
                      placeholder="170"
                    />
                  </div>

                  <div>
                    <Label>{labels.weight}</Label>
                    <Input
                      type="number"
                      value={formData.weight}
                      onChange={(event) => setFieldValue('weight', event.target.value)}
                      placeholder="70"
                    />
                  </div>
                </div>
              </FormSection>

              <FormSection icon={Activity} title={labels.lifestyle}>
                <div className="grid gap-5 md:grid-cols-2">
                  <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900/70">
                    <Checkbox
                      id="patient-smoker"
                      checked={formData.smoker}
                      onCheckedChange={(checked) => setFieldValue('smoker', !!checked)}
                    />
                    <Label htmlFor="patient-smoker" className="cursor-pointer">
                      {labels.smoker}
                    </Label>
                  </div>

                  {formData.smoker ? (
                    <div>
                      <Label>{labels.smokingYears}</Label>
                      <Input
                        type="number"
                        value={formData.smokingYears}
                        onChange={(event) => setFieldValue('smokingYears', event.target.value)}
                        placeholder="5"
                      />
                    </div>
                  ) : null}

                  <div>
                    <Label>{labels.alcohol}</Label>
                    <Select
                      value={formData.alcoholConsumption}
                      onValueChange={(value) => setFieldValue('alcoholConsumption', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={labels.alcohol} />
                      </SelectTrigger>
                      <SelectContent>
                        {alcoholOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {labels[option]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{labels.exercise}</Label>
                    <Select
                      value={formData.exerciseFrequency}
                      onValueChange={(value) => setFieldValue('exerciseFrequency', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={labels.exercise} />
                      </SelectTrigger>
                      <SelectContent>
                        {exerciseOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {labels[option]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </FormSection>

              {(isMedicalOptionsLoading || isMedicalStateLoading) && (
                <div className="flex items-center gap-2 rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-700 dark:border-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-300">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {isMedicalStateLoading ? labels.loadingMedical : labels.loadingSavedOptions}
                </div>
              )}

              <FormSection icon={ListPlus} title={labels.medicalHistory}>
                <div className="grid gap-4">
                  {medicalSections.map((section) => (
                    <MedicalItemsField
                      key={section.key}
                      title={section.title}
                      hint={section.hint}
                      items={formData[section.key]}
                      inputValue={tagInputs[section.key]}
                      onInputChange={(value) => setTagInputValue(section.key, value)}
                      onAdd={() => addMedicalItem(section.key)}
                      onRemove={(value) => removeMedicalItem(section.key, value)}
                      onChooseSuggestions={() => setOptionsPicker(section.key)}
                      hasSuggestions={medicalOptions[section.key].length > 0}
                      labels={labels}
                    />
                  ))}
                </div>

                <div className="mt-5">
                  <Label>{labels.note}</Label>
                  <Textarea
                    value={formData.familyHistory}
                    onChange={(event) => setFieldValue('familyHistory', event.target.value)}
                    placeholder={labels.note}
                    rows={4}
                  />
                </div>
              </FormSection>
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              variant="outline"
              disabled={isSubmitting || isResendingOtp}
              onClick={() => {
                if (verificationStep) {
                  setVerificationStep(null);
                  setOtpCode('');
                  setOtpError('');
                  return;
                }
                resetDialogState();
              }}
            >
              {labels.cancel}
            </Button>
            {verificationStep ? (
              <>
                <Button
                  variant="outline"
                  disabled={isSubmitting || isResendingOtp}
                  onClick={handleResendOtp}
                >
                  {isResendingOtp ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {labels.resendOtp}
                    </>
                  ) : (
                    labels.resendOtp
                  )}
                </Button>
                <Button
                  disabled={isSubmitting}
                  className="bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                  onClick={handleVerifyOtp}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {labels.saving}
                    </>
                  ) : (
                    labels.verify
                  )}
                </Button>
              </>
            ) : (
              <Button
                disabled={isSubmitting}
                className="bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {labels.saving}
                  </>
                ) : (
                  editingMember ? labels.saveChanges : labels.addPatient
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {optionsPicker ? (
        <SavedOptionsDialog
          open={!!optionsPicker}
          onOpenChange={(open) => {
            if (!open) {
              setOptionsPicker(null);
            }
          }}
          title={
            medicalSections.find((section) => section.key === optionsPicker)?.title ||
            labels.chooseFromSavedOptions
          }
          options={medicalOptions[optionsPicker] || []}
          selectedItems={formData[optionsPicker] || []}
          onSelect={(value) => addMedicalItem(optionsPicker, value)}
          labels={labels}
        />
      ) : null}
    </div>
  );
};

export default FamilyMembersPage;
